import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import moment, { Moment } from "moment";
import "moment/locale/tr";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PaymentType } from "../../Models/PaymentType";
import { RefCodeType } from "../../Models/ReferenceCodes";
import { ROUTE_PATHS } from "../../Routes/constants";
import { bulkAndSinglePaymentLinkGenerate } from "../../Services/BankService";
import { calculateSnoozeFee } from "../../Services/CreditService";
import { sendEvent } from "../../Utils/analytics";
import {
  getRemoteText,
  getRemoteValue,
  getRemoteValueVariable,
} from "../../Utils/firebase";
import useAuthState from "../../Utils/store";
import CLoadingButton from "../CLoadingButton/CLoadingButton";
import { FormatAmountSymbol } from "../FormatData/FormatAmount";
import FormatDateToTR from "../FormatData/FormatDate";

const WEEKDAY_LABELS = ["PZT", "SAL", "ÇAR", "PER", "CUM", "CMT", "PAZ"];
const PAYMENT_DOT = "#FF8A00";
const INK = "#191B1E";
const TEXT_MUTED = "#76808A";
const BORDER = "#EBEEF3";

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dateKey(d: Date): string {
  return moment(d).format("YYYY-MM-DD");
}

function diffDays(from: Date, to: Date): number {
  const a = moment(startOfDay(from));
  const b = moment(startOfDay(to));
  return b.diff(a, "days");
}

interface SnoozeDialogCalendarProps {
  open: boolean;
  onClose: () => void;
  debt: {
    id: number;
    creditId: number;
    maxSnoozeDuration: number;
    dueDate: Date;
    isLate: boolean;
    /** CRL-3241: remaining amount shown in the payments panel + summary. */
    amount?: number;
    shop?: {
      name: string;
      logoUrl?: string;
    };
  };
  /** Dates to show with a red badge on the calendar */
  badgeDates?: Date[];
}

/**
 * CRL-3241: single-payment snooze now follows the new-design.html defer flow:
 * step 1 — calendar with the payment listed on the right ("Ertelenecek
 * ödemeler"), step 2 — "Erteleme Özeti" with the agreement + pay button.
 * All pre-existing logic (date rules, fee calculation, analytics, masterpass
 * link generation) is unchanged.
 */
const SnoozeDialogCalendar = ({
  open,
  onClose,
  debt,
  badgeDates = [],
}: SnoozeDialogCalendarProps) => {
  const navigate = useNavigate();
  const { setPaymentMethod } = useAuthState();

  const [step, setStep] = useState<"calendar" | "summary">("calendar");
  const [loading, setLoading] = useState(false);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [feeData, setFeeData] = useState<{
    dueDate: string;
    snoozeFee: number;
  } | null>(null);

  // Calendar state
  const baseDate = useMemo(
    () => startOfDay(debt.isLate ? new Date() : new Date(debt.dueDate)),
    [debt.dueDate, debt.isLate]
  );

  const rangeStart = useMemo(() => {
    const d = new Date(baseDate);
    d.setDate(d.getDate() + 1);
    return startOfDay(d);
  }, [baseDate]);

  const rangeEnd = useMemo(() => {
    const d = new Date(baseDate);
    d.setDate(d.getDate() + debt.maxSnoozeDuration);
    return startOfDay(d);
  }, [baseDate, debt.maxSnoozeDuration]);

  const initialDate = useMemo(() => {
    const d = new Date(rangeStart);
    return startOfDay(d);
  }, [rangeStart]);

  const [viewMoment, setViewMoment] = useState(() =>
    moment(initialDate).startOf("month")
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const badgeKeys = useMemo(() => {
    const set = new Set<string>();
    badgeDates.forEach((d) => {
      set.add(dateKey(startOfDay(d)));
    });
    return set;
  }, [badgeDates]);

  // Reset state when dialog opens — pre-select initialDate (rangeStart + 1)
  useEffect(() => {
    if (!open) return;
    setStep("calendar");
    setAgreementChecked(false);
    setFeeData(null);
    setSelectedDate(initialDate);
    setViewMoment(moment(initialDate).startOf("month"));
  }, [open, initialDate]);

  const selectedDays = useMemo(() => {
    if (!selectedDate) return 0;
    return diffDays(baseDate, selectedDate);
  }, [selectedDate, baseDate]);

  const getDayDifference = useMemo(() => {
    if (!selectedDate) return 0;
    return diffDays(debt.dueDate, selectedDate);
  }, [selectedDays]);

  // Calculate fee when a date is selected
  useEffect(() => {
    if (!open || !selectedDate || selectedDays <= 0) return;

    const calculateFee = async () => {
      setCalculationLoading(true);
      try {
        const response = await calculateSnoozeFee(debt.id, selectedDays);
        setFeeData(response);
      } catch {
        setFeeData(null);
      } finally {
        setCalculationLoading(false);
      }
    };

    calculateFee();
  }, [open, selectedDate, selectedDays, debt.id]);

  const isSelectable = useCallback(
    (day: Date) => {
      const t = startOfDay(day);
      return t >= rangeStart && t <= rangeEnd;
    },
    [rangeStart, rangeEnd]
  );

  const canGoPrev = useMemo(() => {
    const firstOfView = viewMoment.clone().startOf("month");
    return firstOfView.isAfter(moment(rangeStart).startOf("day"), "day");
  }, [viewMoment, rangeStart]);

  const canGoNext = useMemo(() => {
    const lastOfView = viewMoment.clone().endOf("month");
    return lastOfView.isBefore(moment(rangeEnd).startOf("day"), "day");
  }, [viewMoment, rangeEnd]);

  const calendarCells = useMemo(() => {
    const m = viewMoment.clone().locale("tr");
    const year = m.year();
    const month = m.month();
    const first = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const offset = (first.getDay() + 6) % 7;
    const cells: (number | null)[] = [];
    for (let i = 0; i < offset; i += 1) {
      cells.push(null);
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push(d);
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }
    return { year, month, cells };
  }, [viewMoment]);

  const handleSnooze = async () => {
    if (!agreementChecked || !selectedDate || selectedDays <= 0) return;
    setLoading(true);
    try {
      setPaymentMethod({
        type: 3,
        creditId: debt.creditId,
        snoozeDetails: {
          snoozeDays: selectedDays,
          snoozeFee: feeData?.snoozeFee,
          newPaymentDate: feeData?.dueDate,
          creditId: debt.creditId,
          merchantName: debt.shop?.name || "",
          merchantLogo: debt.shop?.logoUrl || "",
        },
      });

      await handleMasterpassRedirect(
        RefCodeType.SNOOZE,
        {
          snoozeDays: selectedDays,
          snoozeFee: feeData?.snoozeFee || 0,
        },
        debt.creditId
      );

      onClose();
    } catch (error) {
      console.error("Snooze error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMasterpassRedirect = async (
    type: RefCodeType,
    snoozeDetails: { snoozeFee: number; snoozeDays: number },
    creditId: number
  ) => {
    const paymentType = type || RefCodeType.ALL;
    if (paymentType == RefCodeType.SNOOZE) {
      sendEvent(`click_make_snooze_payment_button`, {
        amount: snoozeDetails?.snoozeFee,
        snoozedDay: snoozeDetails?.snoozeDays,
      });
    }
    setLoading(true);
    if (creditId) {
      const paymentType =
        type === RefCodeType.SNOOZE ? PaymentType.SNOOZE : PaymentType.SINGLE;
      const response = await bulkAndSinglePaymentLinkGenerate({
        type: paymentType,
        creditId: creditId,
        snoozeDays:
          type === RefCodeType.SNOOZE ? snoozeDetails?.snoozeDays : undefined,
      }).finally(() => {
        setLoading(false);
      });
      if (response) {
        navigate(ROUTE_PATHS.PAYMENT_FRAME, {
          state: {
            url: response.url,
            token: response.token,
            type: paymentType,
          },
        });
      }
    }
  };

  const handleDateSelect = (cellDate: Date) => {
    if (!isSelectable(cellDate)) return;
    setSelectedDate(cellDate);
    sendEvent("postpone_day_selected", {
      snooze_day: diffDays(baseDate, cellDate),
      input_method: "calendar",
    });
  };

  const feeText =
    !selectedDate || calculationLoading
      ? "..."
      : feeData?.snoozeFee != null
        ? FormatAmountSymbol(feeData.snoozeFee)
        : "-";

  const newTotalText =
    !selectedDate || calculationLoading
      ? "..."
      : debt.amount != null && feeData?.snoozeFee != null
        ? FormatAmountSymbol(debt.amount + feeData.snoozeFee)
        : "-";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={step === "calendar" ? "md" : "sm"}
      fullWidth
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          m: { xs: 1, md: 2 },
          maxWidth: step === "calendar" ? "720px" : "500px",
          width: "100%",
        },
      }}
    >
      {/* Title */}
      <Box sx={{ position: "relative", pt: 3, pb: 1, px: 2 }}>
        {step === "summary" && (
          <IconButton
            aria-label="back"
            onClick={() => setStep("calendar")}
            sx={{ position: "absolute", left: 8, top: 8, color: INK }}
          >
            <ArrowBackOutlinedIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}
        <Typography
          align="center"
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            color: INK,
            pr: 4,
            pl: 4,
          }}
        >
          {step === "calendar"
            ? getRemoteValue("snooze_calendar_title") || "Erteleyeceğin Günü Seç"
            : getRemoteText("bulk_snooze_summary_title", "Erteleme Özeti")}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[700],
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {step === "calendar" ? (
        <DialogContent sx={{ pt: 2, px: { xs: 2, md: 4 }, pb: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 2.5, md: 3 }}
            sx={{ width: "100%" }}
          >
            {/* Calendar */}
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
              {/* Month navigation */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: INK,
                    textTransform: "capitalize",
                  }}
                >
                  {viewMoment.clone().locale("tr").format("MMMM YYYY")}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    disabled={!canGoPrev}
                    onClick={() =>
                      setViewMoment((vm: Moment) =>
                        vm.clone().subtract(1, "month")
                      )
                    }
                    aria-label="previous month"
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={!canGoNext}
                    onClick={() =>
                      setViewMoment((vm: Moment) => vm.clone().add(1, "month"))
                    }
                    aria-label="next month"
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              {/* Weekday headers */}
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  {WEEKDAY_LABELS.map((label) => (
                    <Typography
                      key={label}
                      align="center"
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "#B5BEC6",
                        textTransform: "uppercase",
                      }}
                    >
                      {label}
                    </Typography>
                  ))}
                </Box>

                {/* Calendar grid */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: "6px",
                  }}
                >
                  {calendarCells.cells.map((day, idx) => {
                    if (day === null) {
                      return (
                        <Box key={`e-${idx}`} sx={{ height: { xs: 34, md: 40 } }} />
                      );
                    }
                    const cellDate = new Date(
                      calendarCells.year,
                      calendarCells.month,
                      day
                    );
                    const selectable = isSelectable(cellDate);
                    const selected =
                      selectedDate != null &&
                      sameCalendarDay(cellDate, selectedDate);
                    const key = dateKey(cellDate);
                    const showBadge = badgeKeys.has(key);

                    return (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: { xs: 36, md: 44 },
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: { xs: 32, md: 36 },
                            height: { xs: 32, md: 36 },
                          }}
                        >
                          {showBadge && (
                            <Box
                              aria-hidden
                              sx={{
                                position: "absolute",
                                top: 6,
                                right: 6,
                                width: 4,
                                height: 4,
                                borderRadius: "50%",
                                bgcolor: PAYMENT_DOT,
                                zIndex: 1,
                                pointerEvents: "none",
                              }}
                            />
                          )}
                          <Box
                            role="button"
                            tabIndex={selectable ? 0 : -1}
                            onClick={() => handleDateSelect(cellDate)}
                            onKeyDown={(e) => {
                              if (
                                selectable &&
                                (e.key === "Enter" || e.key === " ")
                              ) {
                                e.preventDefault();
                                handleDateSelect(cellDate);
                              }
                            }}
                            sx={{
                              width: { xs: 32, md: 36 },
                              height: { xs: 32, md: 36 },
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: selectable ? "pointer" : "default",
                              bgcolor: selected ? INK : "transparent",
                              color: selected
                                ? "#fff"
                                : selectable
                                  ? INK
                                  : "#EAECED",
                              fontSize: { xs: "0.8rem", md: "0.875rem" },
                              fontWeight: 600,
                              outline: "none",
                              "&:focus-visible": selectable
                                ? {
                                  boxShadow:
                                    "0 0 0 2px rgba(25,27,30,0.35)",
                                }
                                : {},
                            }}
                          >
                            {day}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Legend */}
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: PAYMENT_DOT,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: "0.75rem", color: "#64748B" }}>
                  {getRemoteValue("snooze_calendar_legend") ||
                    "Ödeme tarihini gösterir."}
                </Typography>
              </Stack>
            </Stack>

            {/* Ertelenecek ödemeler (this payment) */}
            <Stack
              sx={{
                flex: 1,
                minWidth: { md: "260px" },
                borderLeft: { md: `1px solid ${BORDER}` },
                borderTop: { xs: `1px solid ${BORDER}`, md: "none" },
                pl: { md: 3 },
                pt: { xs: 2.5, md: 0 },
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} pb={1.5}>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "0.9rem", color: INK }}
                >
                  {getRemoteText(
                    "bulk_snooze_payments_title",
                    "Ertelenecek ödemeler"
                  )}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#ECEDEF",
                    color: INK,
                    borderRadius: "999px",
                    minWidth: "22px",
                    height: "22px",
                    px: "6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                  }}
                >
                  1
                </Box>
              </Stack>
              <Stack sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} py={1.5}>
                  {debt.shop?.logoUrl && (
                    <img
                      src={debt.shop.logoUrl}
                      alt={debt.shop?.name || ""}
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "9px",
                        objectFit: "cover",
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Stack sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      noWrap
                      sx={{ fontSize: "0.85rem", fontWeight: 600, color: INK }}
                    >
                      {debt.shop?.name || ""}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: "#93A0B5" }}>
                      {getRemoteText("due_date_prefix", "Vade")}{" "}
                      {moment(debt.dueDate).locale("tr").format("D MMMM YYYY")}
                    </Typography>
                  </Stack>
                  {debt.amount != null && (
                    <Typography
                      sx={{ fontSize: "0.85rem", fontWeight: 700, color: INK }}
                    >
                      {FormatAmountSymbol(debt.amount)}
                    </Typography>
                  )}
                </Stack>
              </Stack>
              {debt.amount != null && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ borderTop: `1px solid ${BORDER}`, pt: 1.5, mt: 0.5 }}
                >
                  <Typography sx={{ fontSize: "0.85rem", color: "#64748B" }}>
                    {getRemoteText("total", "Toplam")}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "1rem", fontWeight: 800, color: INK }}
                  >
                    {FormatAmountSymbol(debt.amount)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Stack>

          <Box sx={{ pt: 3 }}>
            <CLoadingButton
              fullWidth
              size="large"
              variant="contained"
              disabled={!selectedDate || selectedDays <= 0}
              onClick={() => {
                sendEvent("click_continue_snooze_button");
                setStep("summary");
              }}
            >
              {getRemoteText(
                "bulk_snooze_confirm_button",
                "Borçlarını Bu Güne Ertele"
              )}
            </CLoadingButton>
          </Box>
        </DialogContent>
      ) : (
        <DialogContent sx={{ pt: 2, px: { xs: 2.5, md: 4 }, pb: 3 }}>
          {/* Erteleme Özeti */}
          <Stack spacing={2} sx={{ pt: 1 }}>
            <SummaryRow
              label={
                getRemoteValue("new_payment_date") || "Yeni Ödeme Tarihi"
              }
              value={
                !selectedDate || calculationLoading
                  ? "..."
                  : feeData?.dueDate
                    ? FormatDateToTR(feeData.dueDate)
                    : "-"
              }
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography sx={{ fontSize: "0.875rem", color: TEXT_MUTED }}>
                {getRemoteValue("snoozed_days_count") || "Ertelenen Gün Sayısı"}
              </Typography>
              <Typography
                sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#5DA91A" }}
              >
                {!selectedDate
                  ? "..."
                  : getRemoteValueVariable(
                    "will_snooze_x_days",
                    getDayDifference.toString()
                  )}
              </Typography>
            </Stack>
            {debt.amount != null && (
              <SummaryRow
                label={getRemoteValue("snoozed_amount") || "Ertelenen İşlem Tutarı"}
                value={FormatAmountSymbol(debt.amount)}
              />
            )}
            <SummaryRow
              label={
                getRemoteValue("snooze_service_fee") || "Erteleme Hizmet Bedeli"
              }
              value={feeText}
            />
          </Stack>

          {/* Yeni ödenecek toplam */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              borderTop: `1px solid ${BORDER}`,
              borderBottom: `1px solid ${BORDER}`,
              py: 2,
              mt: 2.5,
            }}
          >
            <Typography sx={{ fontSize: "0.875rem", color: TEXT_MUTED }}>
              {getRemoteText("snooze_new_total", "Yeni Ödenecek Toplam")}
            </Typography>
            <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: INK }}>
              {debt.amount != null ? newTotalText : feeText}
            </Typography>
          </Stack>

          {/* Agreement checkbox */}
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreementChecked}
                  onChange={(e) => setAgreementChecked(e.target.checked)}
                  sx={{
                    color: INK,
                    "&.Mui-checked": {
                      color: INK,
                    },
                  }}
                />
              }
              label={
                <Box
                  component="span"
                  sx={{ fontSize: "0.9375rem", color: "#000" }}
                >
                  Colendi{" "}
                  <Link
                    href="#"
                    underline="always"
                    sx={{ color: INK }}
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(
                        getRemoteValue("snooze_agreement_url"),
                        "_blank"
                      );
                    }}
                  >
                    {getRemoteText("snooze_agreement", "Erteleme Sözleşmesi")}
                  </Link>
                  {/* remote value may already carry the 'ni suffix */}
                  {/^.*'n[iı]$/i.test(
                    getRemoteText("snooze_agreement", "Erteleme Sözleşmesi")
                  )
                    ? " okudum, onaylıyorum."
                    : "'ni okudum, onaylıyorum."}
                </Box>
              }
            />
          </Box>

          <Box sx={{ pt: 2 }}>
            <CLoadingButton
              fullWidth
              size="large"
              variant="contained"
              loading={loading}
              onClick={handleSnooze}
              disabled={
                !selectedDate ||
                selectedDays <= 0 ||
                calculationLoading ||
                !agreementChecked
              }
            >
              {feeData?.snoozeFee != null && !calculationLoading
                ? getRemoteText(
                  "snooze_pay_and_defer_x",
                  "{val} öde ve ertele",
                  FormatAmountSymbol(feeData.snoozeFee)
                )
                : getRemoteText("bulk_snooze_summary_pay_card", "Kredi Kartı ile Öde")}
            </CLoadingButton>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography sx={{ fontSize: "0.875rem", color: TEXT_MUTED }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: INK }}>
        {value}
      </Typography>
    </Stack>
  );
}

export default SnoozeDialogCalendar;
